import { useState } from 'react';
import { api } from '../../services/apiClient';
import { Button, Badge } from '../ui';

/**
 * Uniwersalny panel AI-prechecku kwalifikowalności — dla produktów innych niż dotacje_rozwojowe
 * (PUP, KFS, turnusy, pożyczki, wsparcie na starcie, granty FPN).
 * Woła POST /api/crm/applications/:id/ai-precheck (eligibilityPrecheckService + Azure OpenAI).
 * Dla dotacje_rozwojowe używany jest bogatszy GrantPrecheckPanel (RAG).
 */
interface Result {
  verdict: 'kwalifikuje' | 'warunkowo' | 'nie_kwalifikuje' | 'brak_danych';
  score: number;
  summary: string;
  strengths: string[];
  weaknesses: string[];
  missing_info: string[];
  recommendations: string[];
  ai_available: boolean;
}

const VERDICT: Record<string, { label: string; tone: any }> = {
  kwalifikuje: { label: '✓ Kwalifikuje się', tone: 'success' },
  warunkowo: { label: '~ Warunkowo', tone: 'warning' },
  nie_kwalifikuje: { label: '✗ Nie kwalifikuje się', tone: 'danger' },
  brak_danych: { label: '? Za mało danych', tone: 'neutral' },
};

export function UniversalPrecheckPanel({ applicationId }: { applicationId: number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  const run = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.post(`/applications/${applicationId}/ai-precheck`);
      setResult(r.data?.data || r.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Błąd prechecku');
    } finally { setLoading(false); }
  };

  const scoreColor = (s: number) => s >= 70 ? '#15803d' : s >= 40 ? '#d4a017' : '#dc2626';

  return (
    <div className="bg-white rounded-xl border border-navy-100 shadow-sm p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-bold text-navy-900">🤖 Precheck AI — kwalifikowalność</h3>
          <p className="text-xs text-navy-400">Ocena zgodności wniosku z kryteriami programu (Azure OpenAI)</p>
        </div>
        <Button variant="gold" size="sm" loading={loading} onClick={run}>
          {result ? 'Sprawdź ponownie' : 'Sprawdź AI'}
        </Button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}

      {result && !result.ai_available && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">{result.summary}</div>
      )}

      {result && result.ai_available && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge tone={VERDICT[result.verdict]?.tone}>{VERDICT[result.verdict]?.label || result.verdict}</Badge>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-navy-100 rounded-full overflow-hidden">
                <div style={{ width: `${result.score}%`, background: scoreColor(result.score) }} className="h-full" />
              </div>
              <span className="text-sm font-bold" style={{ color: scoreColor(result.score) }}>{result.score}/100</span>
            </div>
          </div>
          {result.summary && <p className="text-sm text-navy-700">{result.summary}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.strengths.length > 0 && (
              <div>
                <div className="text-xs font-bold text-emerald-700 mb-1">Mocne strony</div>
                <ul className="text-xs text-navy-600 list-disc pl-4 space-y-0.5">{result.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {result.weaknesses.length > 0 && (
              <div>
                <div className="text-xs font-bold text-red-700 mb-1">Ryzyka / słabe strony</div>
                <ul className="text-xs text-navy-600 list-disc pl-4 space-y-0.5">{result.weaknesses.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {result.missing_info.length > 0 && (
              <div>
                <div className="text-xs font-bold text-amber-700 mb-1">Brakujące informacje / dokumenty</div>
                <ul className="text-xs text-navy-600 list-disc pl-4 space-y-0.5">{result.missing_info.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {result.recommendations.length > 0 && (
              <div>
                <div className="text-xs font-bold text-indigo-700 mb-1">Rekomendacje</div>
                <ul className="text-xs text-navy-600 list-disc pl-4 space-y-0.5">{result.recommendations.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
          </div>
          <p className="text-[10px] text-navy-300">Ocena orientacyjna wygenerowana przez AI — decyzję podejmuje operator.</p>
        </div>
      )}
    </div>
  );
}
